import React, {Children, memo} from 'react';

import {CONTENT_PADDING} from '../constants';
import {useBreakpoint} from '../hooks';
import {assert, styleMods} from '../utils';
import * as iter from '../utils/iter';

import style from './columns.module.scss';

export default memo(
	/**
	 * @param {{
	 *   columns?: Array<React.ReactNode | Column>,
	 *   children?: React.ReactNode,
	 * }} param0
	 * @returns {React.ReactNode}
	 */
	function Columns({columns, children}) {
		const bp = useBreakpoint();

		// columns with options
		if (columns) {
			const columns2 = makeColumnsIter(columns, bp).toArray();

			if (columns2.length === 0) {
				return null;
			}

			const staticWidths = (() => {
				// one col takes always 100% width
				if (columns2.length === 1) {
					return true;
				}

				// the css code supports up to 12 cols; fallback to dyn
				if (columns2.length > 12) {
					return false;
				}

				// all widths are 1 or default
				return columns2.every(([_, col]) =>
					col.options === null ||
					col.options.width === undefined ||
					col.options.width === 1
				);
			})();

			const widthSum = columns2.reduce(
				(acc, [_, col]) => acc + (col.options?.width || 1),
				0,
			);

			const padding = (columns2.length - 1) / columns2.length * CONTENT_PADDING;

			return (
				<div
					className={styleMods(style, 'base', [
						staticWidths ? 'staticWidths' : 'dynamicWidths',
						staticWidths && `num_${columns2.length}`,
					])}
				>
					{columns2.map(([key, col]) => (
						<div
							key={key}
							className={style.col}
							style={(() => {
								if (staticWidths) {
									return undefined;
								} else if (bp === null || bp.mobileAndDown) {
									return undefined;
								} else {
									const percentage = ((col.options?.width || 1) / widthSum) * 100;
									return {
										width: `calc(${percentage}% - ${padding}px)`
									};
								}
							})()}
						>
							{col.content}
						</div>
					))}
				</div>
			);
		}

		// simple columns
		if (children) {
			const content = Children.map(children, (col) => {
				if (col === null || col === undefined || col === false) {
					return null;
				}
				return (
					<div className={style.col}>
						{col}
					</div>
				);
			});

			const num = (() => {
				if (content === null || content === undefined) {
					return 0;
				}
				assert(Array.isArray(content));
				return content.length;
			})();

			return (
				<div
					className={styleMods(style, 'base', ['staticWidths', `num_${num}`])}
				>
					{content}
				</div>
			);
		}

		return null;
	}
);

/**
 * @param {Array<React.ReactNode | Column>} columns
 * @param {import('../hooks').BreakpointState | null} bp
 * @returns {IterableIterator<[React.Key, Column]>}
 */
function makeColumnsIter(columns, bp) {
	return iter.from(columns)
		// use original index as react key
		.asIndexedPairs()

		.filter(([_, col]) => {
			const content = col instanceof Column ? col.content : col;
			return content !== null && content !== undefined && content !== false;
		})

		.map(
			/** @returns {[React.Key, Column]} ... */
			([i, col]) => {
				// into `Column`
				const col2 = col instanceof Column ? col : new Column(null, col);

				// use original key if there's one
				const key = typeof col2.content === 'object' &&
					col2.content !== null &&
					'key' in col2.content &&
					col2.content.key;

				return [
					key || i,
					col2,
				];
			}
		)

		// filter mob visible cols
		.filter(([_, col]) => {
			if (col.options?.hideOnMobile !== true) {
				return true;
			}

			// hide in ssr to prevent content (images) from loading on mob.
			// the same problem would occur in css as well
			if (bp === null) {
				return false;
			}

			return bp.desktopAndUp;
		});
}

/** @typedef {{
 *   hideOnMobile?: boolean,
 *   width?: number,
 * }} ColumnOptions */

export class Column {
	/**
	 * @param {ColumnOptions | null} options
	 * @param {React.ReactNode} content
	 */
	constructor(options, content) {
		this.options = options;
		this.content = content;
	}
}
